import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";

const REASONS = [
  { value: "general", label: "General Question" },
  { value: "product_support", label: "Product Support" },
  { value: "order_access", label: "Order / Access Issue" },
  { value: "billing", label: "Billing / Payment" },
  { value: "technical", label: "Technical / Website Issue" },
  { value: "review", label: "Send Us a Review" },
  { value: "other", label: "Other" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialState = { name: "", phone: "", email: "", orderId: "", reason: "", message: "", website: "" };

export const ContactForm = () => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Please enter your name.";
    if (!EMAIL_REGEX.test(form.email.trim())) next.email = "Please enter a valid email address.";
    if (!form.reason) next.reason = "Please select a reason for contacting us.";
    if (form.message.trim().length < 2) next.message = "Please enter your message.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          order_id: form.orderId.trim(),
          reason: form.reason,
          message: form.message.trim(),
          website: form.website,
        }),
      });

      if (response.status === 429) {
        setSubmitError("You're submitting too quickly. Please wait a moment and try again.");
        return;
      }
      if (!response.ok) {
        setSubmitError("Something went wrong sending your message. Please try again or email us directly.");
        return;
      }
      setSuccess(true);
    } catch (err) {
      setSubmitError("Something went wrong sending your message. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        className="bg-card border border-brand-border rounded-xl shadow-sm p-8 sm:p-10 text-center"
        data-testid="contact-success-state"
      >
        <CheckCircle2 className="w-12 h-12 text-brand-success mx-auto mb-4" />
        <h3 className="font-display font-black uppercase text-brand-dark text-xl sm:text-2xl mb-3">
          MESSAGE RECEIVED!
        </h3>
        <p className="font-sans text-brand-dark/80 text-sm sm:text-base mb-6">
          EXPECT A RESPONSE WITHIN THE NEXT FEW HOURS..
          <br />
          <br />
          IF THIS IS OUTSIDE OF NORMAL BUSINESS HOURS,
          <br />
          WE WILL RESPOND THE FOLLOWING BUSINESS DAY.
          <br />
          <br />
          WE LOOK FORWARD TO SERVICING YOU, THANK YOU!
        </p>
        <Link
          to="/"
          data-testid="contact-return-home-link"
          className="font-sans font-bold text-brand-red hover:text-brand-cta-hover transition-colors text-sm sm:text-base"
        >
          &larr; RETURN TO HOME
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-card border border-brand-border rounded-xl shadow-sm p-5 sm:p-8 md:p-10" data-testid="contact-form-card">
      <h2 className="font-display font-black uppercase text-brand-dark text-lg sm:text-xl mb-6">SEND US A MESSAGE</h2>

      <form onSubmit={handleSubmit} data-testid="contact-form" noValidate>
        {/* Honeypot field - hidden from real users, bots may fill it */}
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
          <label htmlFor="website">Leave this field blank</label>
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={handleChange("website")}
            data-testid="contact-honeypot-field"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="contact-name" className="block font-sans font-bold text-brand-dark text-sm mb-1.5">
            Name
          </label>
          <input
            id="contact-name"
            type="text"
            value={form.name}
            onChange={handleChange("name")}
            data-testid="contact-name-input"
            className="w-full px-4 py-2.5 rounded-md border border-brand-border bg-white text-brand-dark text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/40 focus:border-brand-red transition-colors"
          />
          {errors.name && (
            <p className="text-brand-red text-xs font-semibold mt-1" data-testid="contact-name-error">
              {errors.name}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="contact-phone" className="block font-sans font-bold text-brand-dark text-sm mb-1.5">
            Phone
          </label>
          <input
            id="contact-phone"
            type="tel"
            value={form.phone}
            onChange={handleChange("phone")}
            data-testid="contact-phone-input"
            className="w-full px-4 py-2.5 rounded-md border border-brand-border bg-white text-brand-dark text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/40 focus:border-brand-red transition-colors"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="contact-email" className="block font-sans font-bold text-brand-dark text-sm mb-1.5">
            Email
          </label>
          <input
            id="contact-email"
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            data-testid="contact-email-input"
            className="w-full px-4 py-2.5 rounded-md border border-brand-border bg-white text-brand-dark text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/40 focus:border-brand-red transition-colors"
          />
          {errors.email && (
            <p className="text-brand-red text-xs font-semibold mt-1" data-testid="contact-email-error">
              {errors.email}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="contact-order-id" className="block font-sans font-bold text-brand-dark text-sm mb-1.5">
            Order ID
          </label>
          <input
            id="contact-order-id"
            type="text"
            value={form.orderId}
            onChange={handleChange("orderId")}
            data-testid="contact-order-id-input"
            className="w-full px-4 py-2.5 rounded-md border border-brand-border bg-white text-brand-dark text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/40 focus:border-brand-red transition-colors"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="contact-reason" className="block font-sans font-bold text-brand-dark text-sm mb-1.5">
            Reason for Contacting
          </label>
          <select
            id="contact-reason"
            value={form.reason}
            onChange={handleChange("reason")}
            data-testid="contact-reason-select"
            className="w-full px-4 py-2.5 rounded-md border border-brand-border bg-white text-brand-dark text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/40 focus:border-brand-red transition-colors"
          >
            <option value="">Select a reason</option>
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          {errors.reason && (
            <p className="text-brand-red text-xs font-semibold mt-1" data-testid="contact-reason-error">
              {errors.reason}
            </p>
          )}
        </div>

        <div className="mb-6">
          <label htmlFor="contact-message" className="block font-sans font-bold text-brand-dark text-sm mb-1.5">
            Message
          </label>
          <textarea
            id="contact-message"
            rows={5}
            value={form.message}
            onChange={handleChange("message")}
            data-testid="contact-message-textarea"
            className="w-full px-4 py-2.5 rounded-md border border-brand-border bg-white text-brand-dark text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/40 focus:border-brand-red transition-colors resize-y"
          />
          {errors.message && (
            <p className="text-brand-red text-xs font-semibold mt-1" data-testid="contact-message-error">
              {errors.message}
            </p>
          )}
        </div>

        {submitError && (
          <p className="text-brand-red text-sm font-semibold mb-4 text-center" data-testid="contact-submit-error">
            {submitError}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit-button"
          className="w-full sm:w-auto sm:mx-auto flex items-center justify-center gap-2 py-3.5 px-10 bg-brand-cta hover:bg-brand-cta-hover text-white rounded-lg font-sans font-extrabold uppercase text-sm sm:text-base tracking-wide transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "SENDING..." : "SEND MESSAGE"}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
};
